import { useCallback, useEffect, useState } from 'react';

import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Clear,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Search,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { useDebouncedCallback } from 'use-debounce';

import { useAppDispatch } from 'store';
import {
  fetchPreviewMensagens,
  PreviewMensagemInterface,
  resetSearchQuery,
  setSearchQuery,
} from 'store/reducers/mensagens';

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    width: '100%',
  },
  adornment: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0.5),
  },
  count: {
    fontSize: 12,
    color: palette.text.secondary,
    minWidth: spacing(4),
    textAlign: 'center',
    userSelect: 'none',
  },
  divider: {
    height: 20,
    margin: spacing(0, 0.5),
  },
  iconButton: {
    padding: 4,
  },
}));

interface HistoryMessagesSearchProps {
  contactId: string;
  channelCode: string;
  onNavigate: (messageId: string) => void;
  onClear: () => void;
  onSearchChange?: (value: string) => void;
}

export default function HistoryMessagesSearch({
  contactId,
  channelCode,
  onNavigate,
  onClear,
  onSearchChange,
}: HistoryMessagesSearchProps) {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [search, setSearch] = useState('');
  const [results, setResults] = useState<PreviewMensagemInterface[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [loading, setLoading] = useState(false);

  const handleSearch = useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setResults([]);
        setCurrentIndex(-1);
        dispatch(resetSearchQuery());
        if (onSearchChange) onSearchChange('');
        onClear();
        return;
      }

      setLoading(true);
      try {
        const previews = await dispatch(
          fetchPreviewMensagens({
            search: term,
            contactId,
            channelCode,
          }),
        ).unwrap();

        setResults(previews);
        if (previews.length === 0) {
          enqueueSnackbar('A pesquisa não retornou nenhuma ocorrência', {
            variant: 'info',
          });
          dispatch(resetSearchQuery());
          if (onSearchChange) onSearchChange('');
          setCurrentIndex(-1);
        } else {
          dispatch(setSearchQuery(term));
          if (onSearchChange) onSearchChange(term);
          setCurrentIndex(0);
          onNavigate(previews[0].id);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error searching messages:', error);
      } finally {
        setLoading(false);
      }
    },
    [
      contactId,
      channelCode,
      dispatch,
      onNavigate,
      onClear,
      onSearchChange,
      enqueueSnackbar,
    ],
  );

  const debouncedSearch = useDebouncedCallback(handleSearch, 1000);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleClear = () => {
    setSearch('');
    setResults([]);
    setCurrentIndex(-1);
    dispatch(resetSearchQuery());
    onClear();
    if (onSearchChange) onSearchChange('');
  };

  const navigateTo = (index: number) => {
    if (index >= 0 && index < results.length) {
      setCurrentIndex(index);
      onNavigate(results[index].id);
    }
  };

  const handleNext = () => navigateTo(currentIndex + 1);
  const handlePrevious = () => navigateTo(currentIndex - 1);

  useEffect(() => {
    handleClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelCode, contactId]);

  const { length } = results;

  return (
    <Box className={classes.root}>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder="Pesquisar mensagem..."
        value={search}
        onChange={handleInputChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Search fontSize="small" color="action" />
              )}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" className={classes.adornment}>
              {!!search && !loading && (
                <>
                  <Typography className={classes.count}>
                    {results.length > 0 ? currentIndex + 1 : 0}/{results.length}
                  </Typography>
                  <IconButton
                    size="small"
                    className={classes.iconButton}
                    onClick={handleNext}
                    disabled={currentIndex >= length - 1 || length === 0}
                  >
                    <KeyboardArrowUp fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    className={classes.iconButton}
                    onClick={handlePrevious}
                    disabled={currentIndex <= 0 || length === 0}
                  >
                    <KeyboardArrowDown fontSize="small" />
                  </IconButton>
                  <Divider orientation="vertical" className={classes.divider} />
                </>
              )}
              {search && (
                <IconButton
                  size="small"
                  className={classes.iconButton}
                  onClick={handleClear}
                >
                  <Clear fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}
